import React, { useState } from "react";
import { defaultImage } from "../../../lib/libraries/imageKit/ImageKit";
import {
  MyMessage,
  InputField,
  Select,
  AddPageShell,
  AddSection,
} from "../../components/Component";
import { codeOk } from "../../../lib";
import { ImageKitUpload } from "../../../ui/ImageKitLibrary";
import { FOLDERS, image_kit_folder } from "../utils/Utils";
import { useGeoPositionContext } from "../utils/geoPosition";
import { useDoSeeDataContext } from "../../../context/DoSeeDataContext";
import { getOneProduct } from "../../../lib/sysNavigation/SysNavigation";
import { _TYPES_ } from "../utils/types";
import Pricing from "../../components/Pricing";
import GenerateDescription from "../utils/GenerateDescription";
import { DescriptionSection } from "../utils/AddSections";
import {
  FiImage,
  FiDollarSign,
  FiSettings,
  FiPackage,
} from "react-icons/fi";

const Edit = () => {
  const { updateOneContent } = useDoSeeDataContext();
  const {
    country_id,
    entry_label,
    country_name,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
  } = useGeoPositionContext();

  const product: any = getOneProduct();
  const [thumbnail, setThumbnail] = useState<any>(product?.images_url);
  const [thumbnailId, setThumbnailId] = useState<any>(product?.images_cdn_ids);
  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState<any>("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const product_id = product?.product_id;
  const [title, setTitle] = useState<any>(product?.name);
  const [category, setCategory] = useState<any>(product?.category);
  const [quantity, setQuantity] = useState<any>(product?.quantity);
  const [productStatus, setProductStatus] = useState<any>(product?.status);
  const [description, setDescription] = useState<any>(product?.description);
  const [pricing, setPricing] = useState<any>(product?.Pricings);
  const [showDescriptionModal, setShowDescriptionModal] = useState<any>(null);

  const productRoutes = "/data/product";

  const responses = (isError: any, message: any) => {
    setError(isError);
    setLoading(false);
    setSuccess(!isError);
    setErrorMessage(message);
  };

  const updateOneProductHandler = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnailId) {
      setLoading(false);
      responses(true, "Image Required");
    } else {
      await sendDBRequest();
    }
  };

  const sendDBRequest = async () => {
    const data = {
      country_id: country_id,
      first_level_id: first_level_id ? first_level_id : 0,
      second_level_id: second_level_id ? second_level_id : 0,
      third_level_id: third_level_id ? third_level_id : 0,
      fourth_level_id: fourth_level_id ? fourth_level_id : 0,
      name: title,
      category: category,
      quantity: quantity,
      images_url: thumbnail ? thumbnail : defaultImage,
      images_cdn_ids: thumbnailId ? thumbnailId : "",
      description: description,
      status: productStatus,
      created_by: product?.managed_by,
      pricing: pricing,
    };

    const { message, status } = await updateOneContent({
      data_id: product_id,
      data: data,
      content: _TYPES_.PRODUCT,
      position: {
        country_id,
        first_level_id,
        second_level_id,
        third_level_id,
        fourth_level_id,
      },
    });

    if (status === codeOk) responses(false, message);
    else responses(true, message);
  };

  const clearMessages = () => {
    setError(null as any);
    setSuccess(null as any);
  };

  return (
    <AddPageShell
      backRoute={productRoutes}
      eyebrow="DoSee Data • Products"
      title="Edit Product"
      subtitle={`Update a product from ${entry_label || "this destination"}.`}
      icon={<FiPackage className="h-5 w-5" />}
      modals={
        showDescriptionModal ? (
          <GenerateDescription
            setDescription={setDescription}
            setShowDescriptionModal={setShowDescriptionModal}
          />
        ) : null
      }
      loading={loading}
      submitLabel="Save Changes"
      onSubmit={updateOneProductHandler}
      onSubmitClick={() => {
        setError(false);
        setSuccess(false);
        setErrorMessage(null);
      }}
      message={
        <MyMessage
          error={error}
          message={message}
          success={success}
          onExpired={() => {
            setError(false);
            setSuccess(false);
          }}
          onClick={() => {
            setError(false);
            setSuccess(false);
            setErrorMessage("");
          }}
        />
      }
    >
      <AddSection
        title="Product Image"
        hint="A clear product photo on a clean background."
        icon={<FiImage className="h-4 w-4" />}
      >
        <ImageKitUpload
          thumbnail={thumbnail}
          thumbnailId={thumbnailId}
          setThumbnail={setThumbnail}
          setThumbnailId={setThumbnailId}
          folder={image_kit_folder(country_name, FOLDERS.PROCUCTS)}
        />
      </AddSection>

      <AddSection title="Product Details" hint="Name, category and stock.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <InputField
            props={{
              id: "title",
              title: "Name",
              type: "text",
              value: title || "",
              onChange: (e: any) => {
                setTitle(e.target.value);
                clearMessages();
              },
            }}
          />
          <Select
            props={{
              id: "category",
              title: "Category",
              option: [
                "Men",
                "Women",
                "Teen",
                "Kids",
                "African shirts",
                "African pants",
                "Beads",
                "Carvings",
                "Handbags",
              ],
              preselect: category ? [{ name: category }] : null,
              onChange: (e: any) => {
                setCategory(e.target.value);
                clearMessages();
              },
            }}
          />
          <InputField
            props={{
              id: "quantity",
              title: "Quantity",
              type: "number",
              value: quantity || "",
              onChange: (e: any) => {
                setQuantity(e.target.value);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Pricing"
        hint="Optional pricing tiers."
        icon={<FiDollarSign className="h-4 w-4" />}
      >
        <Pricing pricing={pricing} setPricing={setPricing} />
      </AddSection>

      <AddSection
        title="Visibility"
        hint="Toggle visibility of this product."
        icon={<FiSettings className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Select
            props={{
              id: "product_status",
              title: "Status",
              required: true,
              option: ["visible", "invisible"],
              preselect:
                productStatus !== undefined && productStatus !== null
                  ? [
                      {
                        name: productStatus === 1 ? "visible" : "invisible",
                      },
                    ]
                  : null,
              onChange: (e: any) => {
                if (e.target.selectedIndex === 0) setProductStatus(1);
                else setProductStatus(0);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <DescriptionSection
        description={description}
        setDescription={setDescription}
        onGenerate={() => setShowDescriptionModal(true)}
        clearMessages={clearMessages}
      />
    </AddPageShell>
  );
};

export default Edit;
